/**
 * "Explain app" — the deterministic, curated answer source for Point & Speak's "ask this". When a reviewer
 * presses an element and asks "what is this / how does it work?", we answer from THIS map, keyed by the
 * element's data-anchor (the stable semantic id). It explains the UI/feature — NOT the user's numbers and
 * NOT advice — so it's safe to serve first-party with no LLM and no RIA exposure (contrast /explain-plan,
 * which explains why a computed number is what it is). Unmapped anchors fall back to a humanized, generic
 * line built from the anchor/element text, so the worst case is still helpful, never a raw key.
 *
 * This is the app-knowledge base. As the surface grows, add entries here (or, when it gets large, back it
 * with retrieval) — the shape stays the same.
 */

import { humanizeKey } from '@/lib/mycio/labels'

export type AppHelp = {
  title: string
  what: string            // what this element/feature IS
  how?: string            // how to use it / what changes it
  askPlan?: boolean       // true => also offer "why is it this value?" (routes to /explain-plan)
}

const ANCHOR_HELP: Record<string, AppHelp> = {
  probability_of_success: {
    title: 'Funded probability',
    what: 'The share of Monte Carlo simulation trials in which your plan achieves your goals. 82% means it succeeded in about 82 of every 100 trials — a probability, not a promise.',
    how: 'It moves when you change savings, retirement age, spending, or your goals.',
    askPlan: true,
  },
  funded_probability: {
    title: 'Funded probability',
    what: 'The probability that your plan achieves your goals, across thousands of simulation trials. Higher means more resilient to weak markets.',
    how: 'Rank your goals or adjust savings/retirement age and watch it move.',
    askPlan: true,
  },
  confidence_band: {
    title: 'Confidence band',
    what: 'A plain-language summary of your funded probability — Secure / On track / At risk / Off track — so you see the headline before the number.',
    how: 'Crossing a band boundary is what matters; small wiggles inside a band do not change the story.',
    askPlan: true,
  },
  typical_future_networth: {
    title: 'Typical future net worth',
    what: 'The median outcome across the simulation trials — half end above it, half below. It is a projection, not a target.',
    how: 'The "harder future" figure beside it shows a tougher-than-usual market, so you see the range, not a single promise.',
    askPlan: true,
  },
}

/** The app-help answer for an anchored element. `anchor` is the data-anchor; text/route are fallbacks. */
export function appHelpFor(anchor: string | null | undefined, elementText?: string | null, route?: string | null): AppHelp {
  const a = (anchor || '').trim()
  if (a && ANCHOR_HELP[a]) return ANCHOR_HELP[a]

  // goal:<goal_key> — the drag-to-rank goal rows. The anchor is the stored key (an address, never a name to show).
  if (a.startsWith('goal:')) {
    return {
      title: 'Goal',
      what: 'A goal in your plan. Its funding odds are computed against your wealth and the goals ranked above it.',
      how: 'Drag goals to rank what matters most — higher-ranked goals are protected first when the plan cannot fund everything.',
      askPlan: true,
    }
  }

  // Fallback: humanize whatever we have so the answer is still useful (never a raw key).
  const label = a ? humanizeKey(a) : (elementText ? `"${elementText.slice(0, 40)}"` : 'this')
  return {
    title: label,
    what: `${label} on the ${route ? `${route} ` : ''}screen. I don't have a written explainer for this element yet.`,
    how: 'If it shows a computed number, ask "why is it this value?" to see what drives it.',
    askPlan: !!a || !!elementText,
  }
}
